// MCO Detail — the deeper analysis, one level below the Scenario page's MCO stress-test grid.
// Exactly four screens, ordered so the math the grid's numbers came from comes first and the
// history last:
//   1. Calculator — the walkthrough behind every cell in the grid (unit, stress sliders,
//      formula boxes).
//   2. Input Cost Tracker — watch the guarantee form: discovery-period progress, margin
//      tracker, and the component-level input-cost legs.
//   3. ECO / MCO Scenario Comparison — answers "Why would MCO pay when ECO does not?":
//      shared price/yield/input-cost conditions, Revenue (what ECO watches) vs Margin
//      (what MCO watches), each trigger, max payment, and estimated payment / % of max.
//   4. Historical Backtest — moved off the Scenario page (see McoBacktestPanel).
// Crop / practice / year context lives in the title; the Scenario page's route params are where
// they are actually selected.

import { useState } from "react";

import CloseOutlined from "@mui/icons-material/CloseOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { formatPrice } from "utility/formatters";

import McoBacktestPanel from "./McoBacktestPanel";
import McoCalculatorTab from "./McoCalculatorTab";
import McoEcoScenarioTab from "./McoEcoScenarioTab";
import McoTrackerChart from "./McoTrackerChart";

// "2025-08-15" -> "Aug 15". Force local-midnight parse — new Date("YYYY-MM-DD") is UTC and
// can print a day early in negative-UTC-offset zones.
const formatWindowDate = (isoDate) =>
  isoDate ? new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "2-digit" }) : "";

const PRICE_TRACK_COLUMNS = [
  { field: "date", headerName: "Date", flex: 1 },
  {
    field: "price",
    headerName: "Price",
    flex: 1,
    align: "right",
    headerAlign: "right",
    valueFormatter: (value) => formatPrice(value, 4),
  },
];

function InputCostTrackerTab({ mco }) {
  const { tracker, priceTrack } = mco;
  const trackerReady = Boolean(tracker?.ready && (tracker.series?.length || tracker.windowSeries?.length));

  return (
    <Box>
      {/* Prefer the mirror's margin tracker (per-bushel + per-acre, MCO's actual guarantee
          forming) over Watts' plain crop-price series — that series only shows a price. */}
      {trackerReady ? (
        <McoTrackerChart
          tracker={tracker}
          priceLegs={mco.priceLegs}
          forecastPaymentPerAcre={mco.projected?.ready ? mco.projected.result.mcoPayment : null}
        />
      ) : !priceTrack?.ready ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {priceTrack?.blockers?.join("; ") || "Price track unavailable."}
        </Alert>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Discovery tracker data isn&apos;t available for this state group — showing Watts crop prices only, not a
            margin view.
          </Alert>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Projected {formatPrice(priceTrack.summary?.projectedPrice, 4)} · {priceTrack.summary?.dayCount} day(s) —{" "}
              {formatWindowDate(priceTrack.summary?.windowStart)} -- {formatWindowDate(priceTrack.summary?.windowEnd)}
            </Typography>
          </Stack>
          {!priceTrack.series?.length ? (
            <Alert severity="info">
              {priceTrack.blockers?.join("; ") || "Daily points unavailable — showing locked projected price only."}
            </Alert>
          ) : (
            <Box sx={{ maxWidth: "500px" }}>
              <TilleyDataGrid
                rows={priceTrack.series}
                columns={PRICE_TRACK_COLUMNS}
                getRowId={(row) => row.date}
                gridKey="mco-price-track"
                defaultPageSize={5}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </Box>
          )}
        </Box>
      )}

      {/* The tracker's own contract ledger supersedes this when it can be drawn — same legs, but
          per day and with each leg's per-bushel contribution. This is the fallback view. */}
      {!trackerReady && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Input-cost components
          </Typography>
          <Table className="reportTable" size="small" sx={{ maxWidth: "700px" }}>
            <TableHead>
              <TableRow>
                <TableCell>Leg</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell align="right">Projected</TableCell>
                <TableCell align="right">Harvest</TableCell>
                <TableCell>Discovery window</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mco.priceLegs?.map((leg) => (
                <TableRow key={leg.leg}>
                  <TableCell>{leg.leg}</TableCell>
                  <TableCell>{leg.symbol}</TableCell>
                  <TableCell align="right">
                    {leg.projectedPrice != null ? formatPrice(leg.projectedPrice, 2) : "--"}
                  </TableCell>
                  <TableCell align="right">
                    {leg.harvestPrice != null ? formatPrice(leg.harvestPrice, 2) : "--"}
                  </TableCell>
                  <TableCell>
                    {leg.projectedWindow?.filter(Boolean).length
                      ? leg.projectedWindow.filter(Boolean).map(formatWindowDate).join(" -- ")
                      : "--"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {Boolean(mco.paymentLedger?.actuarial?.length) && (
        <Stack direction="row" spacing={4} sx={{ mt: 2, flexWrap: "wrap" }}>
          {mco.paymentLedger.actuarial.map((row) => (
            <Stack key={row.label} direction="column">
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography variant="body1">{formatPrice(row.value, 2)}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function McoDetailsDialog({ open, onClose, mco, geo }) {
  const [tab, setTab] = useState(0);

  const context = [
    geo?.commodity_name,
    geo?.county_name && geo?.state_name ? `${geo.county_name}, ${geo.state_name}` : null,
    geo?.practice_name,
    geo?.reinsurance_year,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle variant="h6">
        MCO Detail
        {context && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
            {context}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Calculator" />
          <Tab label="Input Cost Tracker" />
          <Tab label="ECO / MCO Scenario Comparison" />
          <Tab label="Historical Backtest" />
        </Tabs>

        {tab === 0 && <McoCalculatorTab mco={mco} geo={geo} />}
        {tab === 1 && <InputCostTrackerTab mco={mco} />}
        {tab === 2 && <McoEcoScenarioTab mco={mco} />}
        {tab === 3 && <McoBacktestPanel mco={mco} />}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose}>
          <CloseOutlined sx={{ mr: 1 }} />
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
